// lib/auth.ts
//
// Centralise la récupération/rafraîchissement du token de session, pour
// que tout fichier qui a besoin d'un access_token valide (hooks Market,
// futurs hooks d'autres modules) importe cette fonction unique au lieu
// de dupliquer la logique déjà présente dans messages.tsx/actu.tsx.
//
// Reprend fidèlement le comportement de getValidToken() vu dans
// messages.tsx : lecture de la session en cache (AsyncStorage), et
// rafraîchissement automatique via /refresh-token si le token est sur
// le point d'expirer.

import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '@/lib/api';

const SESSION_KEY  = 'harmonia_session';
const TOKEN_MARGIN = 60; // secondes de marge avant expiration réelle

/**
 * Retourne un access_token valide, en le rafraîchissant automatiquement
 * si nécessaire. Retourne null si aucune session valide n'est disponible
 * (l'appelant doit alors rediriger vers /login).
 */
export async function getValidToken(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const session      = JSON.parse(raw);
    const accessToken   = session?.access_token  ?? null;
    const refreshToken  = session?.refresh_token ?? null;
    const expiresAt     = session?.expires_at    ?? 0;

    if (!accessToken || !refreshToken) return null;

    const nowSeconds = Math.floor(Date.now() / 1000);
    if (expiresAt - nowSeconds > TOKEN_MARGIN) {
      // Encore valide pour un moment : pas besoin de rafraîchir
      return accessToken;
    }

    // Token expiré ou sur le point de l'être : on rafraîchit
    const res = await fetch(`${API_BASE_URL}/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) return null;

    const { access_token, refresh_token: newRefresh, expires_at } = await res.json();

    await AsyncStorage.setItem(SESSION_KEY, JSON.stringify({
      ...session,
      access_token,
      refresh_token: newRefresh ?? refreshToken,
      expires_at,
    }));

    return access_token;
  } catch {
    return null;
  }
}
